import { isMemberPlaceCompatible } from './place-compat';
import type { CartItem, MemberAssignment, TeamMember } from '../types';

/** Libre-service (`required_team_members_count = 0`) : aucun intervenant, la ligne sort de l'étape membre. */
export function cartItemNeedsMember(item: CartItem): boolean {
  return (item.prestation.required_team_members_count ?? 1) > 0;
}

/** Nombre de membres requis pour un item (0 = libre-service, géré en amont). */
export function getRequiredMemberCount(item: CartItem): number {
  return Math.max(1, item.prestation.required_team_members_count ?? 1);
}

/**
 * Membres réellement éligibles pour un item : qualifiés pour la prestation (`team_member_ids`
 * vide/absent = tout le monde) ET compatibles avec le lieu sélectionné — mêmes règles que le
 * grisage à l'étape membre. Sert au skip de l'étape membre quand aucun item n'offre de vrai
 * choix, et à l'auto-assignation qui en découle.
 */
export function getEligibleMembersForItem(
  item: CartItem,
  availableMembers: TeamMember[],
  selectedPlaceId: number | undefined,
): TeamMember[] {
  const qualifiedIds = item.prestation.team_member_ids;
  return availableMembers.filter(
    (member): boolean =>
      isMemberPlaceCompatible(member.pro_place_ids, selectedPlaceId) &&
      (!qualifiedIds || qualifiedIds.length === 0 || qualifiedIds.includes(member.team_member_id)),
  );
}

/** Membres choisis pour un article : ses N intervenants, sinon son membre ; vide = « Sans préférence ». */
export const pickedMemberIds = (assignment: MemberAssignment | undefined): number[] =>
  assignment?.memberIds ?? (assignment?.memberId != null ? [assignment.memberId] : []);

/** Nom d'un membre montré au client : son prénom, à défaut son nom. */
export const memberDisplayName = (member: TeamMember): string => member.firstname || member.lastname || '';

/** Noms des membres choisis par le client, sans doublon, dans l'ordre du panier (vide : « Sans préférence »). */
export function chosenMemberNames(assignments: readonly MemberAssignment[], members: readonly TeamMember[]): string[] {
  const ids = [...new Set(assignments.flatMap((assignment): number[] => pickedMemberIds(assignment)))];
  return ids.flatMap((id): string[] => {
    const member = members.find((candidate): boolean => candidate.team_member_id === id);
    const name = member ? memberDisplayName(member) : '';
    return name ? [name] : [];
  });
}
